import { extractTags, firstMarkdownImage } from '../lib/text-format.js';
import { getActiveFile } from '../app/active-file.js';

const titleFilter = 'heading:1';

function isTitle(text) {
  return /^#{1,6}[ \t]/.test(String(text || ''));
}

// タグ結果に画像を並べて出すときは、本文中の `![ラベル](URL)` を省略して
// `![ラベル](…)` にする。URLをそのまま出すと長く、画像と重複する情報のため。
function compactImageMarkdown(text) {
  return String(text || '').replace(/!\[([^\]\n]*)\]\([^)\s]+\)/, '![$1](…)');
}

export function createTagBrowserController({
  state,
  tagBrowserPanel,
  tagBrowserCloseButton,
  tagListEl,
  tagResultsEl,
  tagFilterClearButton,
  tagSearchInput = null,
  tagSortTabs = [],
  menuToggleButton,
  getChildren,
  nodePath,
  shortNodeText,
  syncModalLock,
  setMenuOpen,
  closeNodeMenus,
  activeFilePayload,
  apiGet = null,
  apiPost,
  beforeNavigate = null,
  beforeOpen = null,
  renderTree,
  t,
}) {
  let globalTags = [];
  let globalTagsLoaded = false;
  let globalLoadFailed = false;
  let resultRequestSequence = 0;
  let tagTab = 'current';
  const globalResults = new Map();

  function localTagIndex() {
    const index = new Map();
    for (const node of state.nodes.values()) {
      for (const tag of extractTags(node.text)) {
        if (!index.has(tag)) {
          index.set(tag, []);
        }
        index.get(tag).push(node.id);
      }
    }
    return [...index.entries()];
  }

  function localNodeIds(filter) {
    const ids = [];
    const walk = (id) => {
      const node = state.nodes.get(id);
      const matches =
        node &&
        ((filter === titleFilter && isTitle(node.text)) ||
          (filter !== titleFilter && extractTags(node.text).includes(filter)));
      if (matches) {
        ids.push(id);
      }
      for (const childId of getChildren(id)) {
        walk(childId);
      }
    };
    for (const id of state.rootIds) {
      walk(id);
    }
    return ids;
  }

  function useLocalTags() {
    return tagTab === 'current' || typeof apiGet !== 'function' || globalLoadFailed;
  }

  function visibleTagEntries() {
    if (useLocalTags()) {
      return localTagIndex().map(([tag, ids], index) => ({
        tag,
        node_count: ids.length,
        file_count: 1,
        added_order: index + 1,
      }));
    }
    return globalTags;
  }

  function renderTagBrowser() {
    const titleIds = localNodeIds(titleFilter);
    const query = String(tagSearchInput?.value || '')
      .trim()
      .toLocaleLowerCase('ja');
    const entries = [...visibleTagEntries()].sort((entryA, entryB) => {
      if (tagTab === 'name') {
        return String(entryA.tag || '').localeCompare(String(entryB.tag || ''), 'ja');
      }
      return Number(entryA.added_order || 0) - Number(entryB.added_order || 0);
    });
    const showCurrentNote = tagTab === 'current';
    const availableTags = new Set([...(showCurrentNote ? [titleFilter] : []), ...entries.map((entry) => entry.tag)]);
    if (globalTagsLoaded && state.selectedTag !== null && !availableTags.has(state.selectedTag)) {
      state.selectedTag = null;
    }

    const fragment = document.createDocumentFragment();
    const rows = [
      ...(showCurrentNote ? [{ tag: titleFilter, label: t('tag_title'), count: titleIds.length, fileCount: 1 }] : []),
      ...entries
        .map((entry) => ({
          tag: entry.tag,
          label: entry.label || `#${entry.tag}`,
          count: Number(entry.node_count || 0),
          fileCount: Number(entry.file_count || 0),
        }))
        .filter((row) => query === '' || `${row.label} ${row.tag}`.toLocaleLowerCase('ja').includes(query)),
    ];

    for (const row of rows) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tag-chip';
      button.dataset.tag = row.tag;
      button.setAttribute('aria-pressed', state.selectedTag === row.tag ? 'true' : 'false');
      button.textContent = row.label;
      if (row.tag !== titleFilter && row.fileCount > 0) {
        button.title = t('tag_file_count', { count: row.fileCount });
      }

      const count = document.createElement('span');
      count.className = 'tag-count';
      count.textContent = String(row.count);
      button.append(count);
      fragment.append(button);
    }

    tagListEl.replaceChildren(fragment);
    tagFilterClearButton.hidden = state.selectedTag === null;
    renderTagResults();
  }

  function makeLocalResult(id) {
    const node = state.nodes.get(id);
    if (!node) {
      return null;
    }
    const text = String(node.text || '');
    const chars = Array.from(text);
    return {
      file_id: state.activeFileId,
      file_name: getActiveFile(state)?.name || '',
      node_id: id,
      text_snapshot: chars.slice(0, 150).join(''),
      text_truncated: chars.length > 150,
      path: nodePath(id).map((item) => shortNodeText(item.text)),
    };
  }

  function appendResult(list, result) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tag-result-item';
    button.dataset.nodeId = String(result.node_id);
    button.dataset.fileId = String(result.file_id);

    const fileName = document.createElement('span');
    fileName.className = 'tag-result-file';
    fileName.textContent = result.file_name || t('file_untitled');

    const text = document.createElement('span');
    text.className = 'tag-result-text';
    const snapshot = String(result.text_snapshot || '');
    const suffix = result.text_truncated ? '…' : '';

    // 画像を含む結果は、正方形の画像を左に、テキストを右に並べる。
    const image = state.inlineImagesEnabled ? firstMarkdownImage(snapshot) : null;
    const displayText = image ? compactImageMarkdown(snapshot) : snapshot;
    text.textContent = displayText.trim() === '' ? t('markdown_empty') : displayText + suffix;

    const path = document.createElement('span');
    path.className = 'tag-result-path';
    path.textContent = Array.isArray(result.path) ? result.path.join(' › ') : '';

    const textWrap = document.createElement('span');
    textWrap.className = 'tag-result-text-wrap';
    textWrap.append(fileName, text, path);

    button.classList.add('has-image');
    if (image) {
      const img = document.createElement('img');
      img.className = 'tag-result-image';
      img.src = image.url;
      img.alt = '';
      img.loading = 'lazy';
      button.append(img, textWrap);
    } else {
      // 画像が無い結果も枠を揃えるため、同じ大きさのプレースホルダを出す。
      const placeholder = document.createElement('span');
      placeholder.className = 'tag-result-image tag-result-image-placeholder';
      placeholder.textContent = '#';
      placeholder.setAttribute('aria-hidden', 'true');
      button.append(placeholder, textWrap);
    }
    list.append(button);
  }

  function renderTagResults() {
    tagResultsEl.replaceChildren();
    if (state.selectedTag === null) {
      tagResultsEl.hidden = true;
      return;
    }

    const title = document.createElement('h3');
    title.textContent = state.selectedTag === titleFilter ? t('tag_title') : `#${state.selectedTag}`;
    const list = document.createElement('div');
    list.className = 'tag-result-list';

    if (state.selectedTag === titleFilter || useLocalTags()) {
      const results = localNodeIds(state.selectedTag).map(makeLocalResult).filter(Boolean);
      for (const result of results) {
        appendResult(list, result);
      }
      if (results.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'tag-empty';
        empty.textContent = t('tag_no_results');
        list.append(empty);
      }
    } else {
      const resultState = globalResults.get(state.selectedTag);
      if (!resultState || (resultState.loading && resultState.items.length === 0)) {
        const loading = document.createElement('p');
        loading.className = 'tag-empty';
        loading.textContent = t('status_loading');
        list.append(loading);
      } else if (resultState.error) {
        const error = document.createElement('p');
        error.className = 'tag-empty';
        error.textContent = t('tag_load_failed');
        list.append(error);
      } else {
        for (const result of resultState.items) {
          appendResult(list, result);
        }
        if (resultState.items.length === 0) {
          const empty = document.createElement('p');
          empty.className = 'tag-empty';
          empty.textContent = t('tag_no_results');
          list.append(empty);
        }
        if (resultState.nextCursor !== null) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'secondary-button tag-results-more';
          more.dataset.tag = state.selectedTag;
          more.textContent = resultState.loading ? t('status_loading') : t('tag_load_more');
          more.disabled = resultState.loading;
          list.append(more);
        }
      }
    }

    tagResultsEl.hidden = false;
    tagResultsEl.replaceChildren(title, list);
  }

  async function loadGlobalTags() {
    if (typeof apiGet !== 'function') {
      globalTagsLoaded = true;
      renderTagBrowser();
      return;
    }
    globalLoadFailed = false;
    try {
      const data = await apiGet('api/tags.php');
      globalTags = Array.isArray(data.tags) ? data.tags : [];
      globalTagsLoaded = true;
    } catch (error) {
      globalLoadFailed = true;
      globalTagsLoaded = true;
      console.error(error);
    }
    renderTagBrowser();
    if (state.selectedTag && state.selectedTag !== titleFilter && !useLocalTags()) {
      await loadTagResults(state.selectedTag, true);
    }
  }

  async function loadTagResults(tag, reset = false) {
    if (!tag || tag === titleFilter || useLocalTags()) {
      renderTagResults();
      return;
    }
    const previous = globalResults.get(tag) || { items: [], nextCursor: null, loading: false, error: null };
    if (previous.loading) {
      return;
    }
    const requestSequence = ++resultRequestSequence;
    const next = reset
      ? { items: [], nextCursor: null, loading: true, error: null }
      : { ...previous, loading: true, error: null };
    globalResults.set(tag, next);
    if (state.selectedTag === tag) {
      renderTagResults();
    }

    try {
      const cursor = reset ? '0' : previous.nextCursor || '0';
      const data = await apiGet(
        `api/tag_results.php?tag=${encodeURIComponent(tag)}&limit=100&cursor=${encodeURIComponent(cursor)}`
      );
      if (requestSequence !== resultRequestSequence && state.selectedTag !== tag) {
        return;
      }
      globalResults.set(tag, {
        items: reset ? data.results || [] : [...previous.items, ...(data.results || [])],
        nextCursor: data.next_cursor ?? null,
        loading: false,
        error: null,
      });
    } catch (error) {
      globalResults.set(tag, { ...next, loading: false, error });
      console.error(error);
    }
    if (state.selectedTag === tag) {
      renderTagResults();
    }
  }

  async function selectTag(tag) {
    state.selectedTag = tag || null;
    renderTagBrowser();
    if (state.selectedTag && state.selectedTag !== titleFilter) {
      await loadTagResults(state.selectedTag, true);
    }
    tagResultsEl.querySelector('.tag-result-item, .tag-results-more')?.focus();
  }

  async function loadMoreTagResults(tag) {
    await loadTagResults(tag, false);
  }

  async function setTagBrowserOpen(isOpen) {
    tagBrowserPanel.hidden = !isOpen;
    syncModalLock();
    if (isOpen) {
      state.tagBrowserLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setMenuOpen(false);
      closeNodeMenus();
      renderTagBrowser();
      (tagSearchInput || tagListEl.querySelector('.tag-chip') || tagBrowserCloseButton).focus();
      if (typeof beforeOpen === 'function') {
        try {
          await beforeOpen();
        } catch (error) {
          console.error(error);
        }
      }
      await loadGlobalTags();
      return;
    }
    if (state.tagBrowserLastFocus && state.tagBrowserLastFocus.offsetParent !== null) {
      state.tagBrowserLastFocus.focus();
    } else {
      menuToggleButton.focus();
    }
    state.tagBrowserLastFocus = null;
  }

  tagSearchInput?.addEventListener('input', renderTagBrowser);
  for (const tab of tagSortTabs) {
    tab.addEventListener('click', () => {
      const nextTab = tab.dataset.tagSort;
      tagTab = nextTab === 'name' || nextTab === 'added' ? nextTab : 'current';
      state.selectedTag = null;
      for (const item of tagSortTabs) {
        const selected = item === tab;
        item.classList.toggle('active', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
      }
      renderTagBrowser();
    });
  }

  function closeTagBrowser(restoreFocus = true) {
    tagBrowserPanel.hidden = true;
    syncModalLock();
    if (!restoreFocus) {
      state.tagBrowserLastFocus = null;
      return;
    }
    if (state.tagBrowserLastFocus && state.tagBrowserLastFocus.offsetParent !== null) {
      state.tagBrowserLastFocus.focus();
    } else {
      menuToggleButton.focus();
    }
    state.tagBrowserLastFocus = null;
  }

  function expandAncestors(id) {
    const path = nodePath(id);
    for (const node of path.slice(0, -1)) {
      if (node.collapsed) {
        node.collapsed = 0;
        apiPost('api/node_update.php', activeFilePayload({ id: node.id, collapsed: 0 })).catch((error) => {
          console.error(error);
        });
      }
    }
  }

  function focusNodeInTree(id) {
    if (!state.nodes.has(id)) {
      return false;
    }
    expandAncestors(id);
    state.activeNodeId = id;
    closeTagBrowser(false);
    renderTree();
    document.querySelector<HTMLElement>(`.node-text[data-id="${id}"]`)?.focus();
    return true;
  }

  async function openTagResult(fileId, nodeId, query = '') {
    if (Number(fileId) === Number(state.activeFileId)) {
      return focusNodeInTree(Number(nodeId));
    }
    const url = new URL('./', window.location.href);
    url.searchParams.set('file_id', String(fileId));
    url.searchParams.set('focus_node_id', String(nodeId));
    if (query !== '') {
      url.searchParams.set('q', query);
    }
    if (beforeNavigate && !(await beforeNavigate())) return false;
    window.location.href = url.toString();
    return true;
  }

  return {
    renderTagBrowser,
    renderTagResults,
    setTagBrowserOpen,
    closeTagBrowser,
    focusNodeInTree,
    openTagResult,
    selectTag,
    loadMoreTagResults,
  };
}
